// The view model of the Security page (ENTRAID-347 redesign, mockup 2, implementation plan C2): severity and status
// words, the check ordering, the counts behind the KPI row, what each check needs to run, and the drift words.
// Pure functions over the latest run's securityPosture (lambda/security-checks.mjs); no request, no React.

import { AREA_LABELS, categoryOfFinding } from './finding-category.js';

export const SEVERITY_ORDER = Object.freeze({ critical: 0, high: 1, medium: 2, low: 3, info: 4 });
export const SEVERITY_WORD = Object.freeze({ critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' });
/** Status dots only (never a fill, never text color): critical and high bad, medium caution, low and info muted. */
export const SEVERITY_DOT = Object.freeze({ critical: 'var(--bad)', high: 'var(--bad)', medium: 'var(--warn-dot)', low: 'var(--track)', info: 'var(--track)' });

export const STATUS_ORDER = Object.freeze({ fail: 0, warn: 1, pass: 2, skip: 3 });
export const STATUS_WORD = Object.freeze({ fail: 'Failed', warn: 'Warning', pass: 'Passed', skip: 'Not run' });
export const STATUS_DOT = Object.freeze({ fail: 'var(--bad)', warn: 'var(--warn-dot)', pass: 'var(--good)', skip: 'var(--muted)' });

/** The area order of the By area card and the Area filter. */
export const AREA_ORDER = Object.freeze(['identity', 'authentication', 'accessControl', 'applications', 'monitoring']);

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

// What a check needs to run: the Microsoft Graph permission and, where it applies, the license. Moved from the old
// Security page's Skipped checks card (SKIP_PERMISSION_HINTS) so the All checks table says why a check did not run.
export const CHECK_REQUIREMENTS = Object.freeze({
  'AC-005': { permission: 'RoleEligibilitySchedule.Read.Directory', license: 'Entra ID P2' },
  'MON-002': { permission: 'IdentityRiskyUser.Read.All', license: 'Entra ID P2' },
  'MON-001': { permission: 'IdentityRiskEvent.Read.All', license: 'Entra ID P2' },
  'AUTH-001': { permission: 'UserAuthenticationMethod.Read.All' },
  'AUTH-002': { permission: 'UserAuthenticationMethod.Read.All' },
  'AUTH-003': { permission: 'UserAuthenticationMethod.Read.All' },
  'AUTH-004': { permission: 'UserAuthenticationMethod.Read.All' },
  'AUTH-005': { permission: 'UserAuthenticationMethod.Read.All' },
  'MON-003': { permission: 'SecurityEvents.Read.All' },
  'MON-004': { permission: 'DirectoryRecommendations.Read.All' },
  'MON-005': { permission: 'Device.Read.All' },
  'MON-006': { permission: 'Device.Read.All' },
  'AC-001': { permission: 'Policy.Read.All' },
  'AC-002': { permission: 'Policy.Read.All' },
  'AC-003': { permission: 'Policy.Read.All' },
  'AC-006': { permission: 'Policy.Read.All' },
  'AC-007': { permission: 'Policy.Read.All' },
  'AC-008': { permission: 'Policy.Read.All' },
  'APP-001': { permission: 'Application.Read.All' },
  'APP-002': { permission: 'DelegatedPermissionGrant.ReadWrite.All' },
  'APP-003': { permission: 'Application.Read.All' },
  'APP-004': { permission: 'Application.Read.All' },
  'APP-005': { permission: 'DelegatedPermissionGrant.ReadWrite.All' },
  'APP-006': { permission: 'Application.Read.All' },
  'IDENT-007': { permission: 'User.Read.All + Directory.Read.All' },
  'AC-009': { permission: 'Organization.Read.All + Policy.Read.All' },
  'AUTH-006': { permission: 'Domain.Read.All' },
  'MON-007': { permission: 'AuditLog.Read.All' },
  'IDENT-008': { permission: 'Group.Read.All' },
  'AC-010': { permission: 'Policy.Read.All' },
  'AC-011': { permission: 'Organization.Read.All + Policy.Read.All' },
  'APP-007': { permission: 'Organization.Read.All + Policy.Read.All' },
  'APP-008': { permission: 'Application.Read.All' },
  'IDENT-009': { permission: 'RoleManagement.Read.Directory' },
  'MON-008': { permission: 'AuditLog.Read.All' },
});

/** Why a check did not run, in words: "Needs IdentityRiskyUser.Read.All and Entra ID P2". */
export function notRunReason(id) {
  const req = CHECK_REQUIREMENTS[id];
  if (!req) return 'The data this check reads was not collected';
  return `Needs ${req.permission}${req.license ? ` and ${req.license}` : ''}`;
}

/** The area name of a check ("Access control"), or null for an unknown prefix. */
export function areaOf(finding) {
  return AREA_LABELS[categoryOfFinding(finding?.id)] ?? null;
}

/** All checks, severity first and then status (stable). The table's default order. */
export function orderChecks(findings) {
  return (Array.isArray(findings) ? findings : [])
    .map((f, i) => ({ f, i }))
    .sort((a, b) => ((SEVERITY_ORDER[a.f.severity] ?? 5) - (SEVERITY_ORDER[b.f.severity] ?? 5))
      || ((STATUS_ORDER[a.f.status] ?? 4) - (STATUS_ORDER[b.f.status] ?? 4))
      || (a.i - b.i))
    .map(({ f }) => f);
}

/** The failing checks, most severe first (stable). Fix first shows the first `n`. */
export function failingChecks(findings) {
  return orderChecks(findings).filter((f) => f?.status === 'fail');
}

/** Counts per status and the total, from the findings (the run's counters when the findings are missing). */
export function checkCounts(posture) {
  const findings = Array.isArray(posture?.findings) ? posture.findings : [];
  const by = (s) => findings.filter((f) => f?.status === s).length;
  const counts = findings.length
    ? { fail: by('fail'), warn: by('warn'), pass: by('pass'), skip: by('skip') }
    : { fail: posture?.failCount ?? 0, warn: posture?.warnCount ?? 0, pass: posture?.passCount ?? 0, skip: posture?.skipCount ?? 0 };
  const counted = counts.fail + counts.warn + counts.pass + counts.skip;
  const total = finite(posture?.totalChecks) && posture.totalChecks >= counted ? posture.totalChecks : counted;
  return { ...counts, total, ran: total - counts.skip };
}

/** The severities the "Overall security measures" KPI always shows; info joins them only when the run has one. */
export const MEASURE_SEVERITIES = Object.freeze(['critical', 'high', 'medium', 'low']);

/**
 * Passing, warning and failing evaluated checks per severity (product owner, 2026-10-22), most severe first.
 * Checks that did not run (skip) are not measures here: the Running measures KPI counts them.
 * @returns {Array<{ severity: string, word: string, pass: number, warn: number, fail: number, total: number }>}
 */
export function measuresBySeverity(findings) {
  const list = (Array.isArray(findings) ? findings : []).filter((f) => f && f.status !== 'skip' && f.status in STATUS_ORDER);
  const severities = [...MEASURE_SEVERITIES, ...(list.some((f) => f.severity === 'info') ? ['info'] : [])];
  return severities.map((severity) => {
    const mine = list.filter((f) => f.severity === severity);
    const by = (s) => mine.filter((f) => f.status === s).length;
    return { severity, word: SEVERITY_WORD[severity], pass: by('pass'), warn: by('warn'), fail: by('fail'), total: mine.length };
  });
}

/** The onGuard checks by area, in AREA_ORDER, then any other category the run holds. */
export function areaRows(categories) {
  const cats = categories && typeof categories === 'object' ? categories : {};
  const keys = [...AREA_ORDER.filter((k) => cats[k]), ...Object.keys(cats).filter((k) => !AREA_ORDER.includes(k) && cats[k])];
  return keys.map((key) => {
    const c = cats[key];
    const n = (v) => (finite(v) ? v : 0);
    const parts = [`${n(c.pass)} pass`, `${n(c.warn)} warn`, `${n(c.fail)} fail`];
    if (n(c.skip) > 0) parts.push(`${n(c.skip)} not run`);
    return { key, label: AREA_LABELS[key] ?? key, score: finite(c.score) ? c.score : null, caption: parts.join(', ') };
  });
}

const DRIFT_WORD = Object.freeze({ pass: 'passed', warn: 'warning', fail: 'failed', skip: 'not run' });

/** "Since last collection" in the table: Now failing, Now passing, New, or blank. */
export function sinceLastCollection(change) {
  if (!change) return '';
  if (change.from === 'new') return 'New';
  if (change.to === 'fail') return 'Now failing';
  if (change.to === 'pass') return 'Now passing';
  if (change.to === 'warn') return 'Now a warning';
  if (change.to === 'skip') return 'Not run now';
  return '';
}

/** One drift change in words: "Passed to failed", or "New check, failed". */
export function driftWords(change) {
  const to = DRIFT_WORD[change?.to] ?? String(change?.to ?? '');
  if (change?.from === 'new') return `New check, ${to}`;
  const from = DRIFT_WORD[change?.from] ?? String(change?.from ?? '');
  return `${from.charAt(0).toUpperCase()}${from.slice(1)} to ${to}`;
}

/** The drift changes keyed by check id. */
export function driftById(posture) {
  const list = Array.isArray(posture?.drift?.changes) ? posture.drift.changes : [];
  return new Map(list.filter((c) => c && typeof c.id === 'string').map((c) => [c.id, c]));
}
